/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, ArrowRight, ArrowUpRight, Maximize2 } from 'lucide-react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useHeaderStates } from '../../Providers/HeaderStates';
import { useStartupState } from '../../Providers/StartupState';
import { Loading } from '../../components/Loading';
import { Visualizador } from '../PhotoGallery/Visualizador';
import { FotoEnquadrada } from '../PhotoGallery';
import { Foto } from '../Sustainability/comum';
import { enderecoDa } from '.';
import {
  ROTULOS,
  aplicarMetadados,
  carregarMateria,
  carregarNoticias,
  dataPorExtenso,
  enderecoDaAntiga,
  minutosDeLeitura,
  resumoRepeteOTexto,
} from './dados';
import * as S from './style';

// Barra amarela no topo que enche conforme a leitura da matéria.
const BarraDeLeitura = ({ alvo }) => {
  const [largura, setLargura] = useState(0);
  useEffect(() => {
    const medir = () => {
      const el = alvo.current;
      if (!el) return;
      const total = Math.max(1, el.offsetTop + el.offsetHeight - window.innerHeight);
      setLargura(Math.min(100, Math.max(0, (window.scrollY / total) * 100)));
    };
    medir();
    window.addEventListener('scroll', medir, { passive: true });
    window.addEventListener('resize', medir);
    return () => {
      window.removeEventListener('scroll', medir);
      window.removeEventListener('resize', medir);
    };
  }, [alvo]);
  return <S.Progresso style={{ width: `${largura}%` }} aria-hidden="true" />;
};

// Fotos no meio do texto. Duas lado a lado; deitadas ficam mais baixas, para
// não sobrar faixa vazia (a proporção só se sabe quando a foto carrega).
const BlocoFotos = ({ fotos, inicio, aoAbrir }) => {
  const [proporcoes, setProporcoes] = useState({});
  const quantas = fotos.length;
  const medidas = Object.values(proporcoes);
  const deitadas = medidas.length === quantas && medidas.every((p) => p > 1.2);
  return (
    <S.Fotos $quantas={quantas} $deitadas={deitadas}>
      {fotos.map((f, i) => (
        <figure key={`${f.id}-${i}`}>
          <button
            type="button"
            onClick={() => aoAbrir(inicio + i)}
            aria-label={`${ROTULOS.ampliar}${f.legenda ? `: ${f.legenda}` : ''}`}
          >
            {/* Foto (e não FotoEnquadrada, que tem o próprio onLoad): aqui o onLoad
                também mede a proporção. */}
            <Foto
              id={f.id}
              largura={quantas === 1 ? 1400 : 900}
              alt={f.legenda || ''}
              onLoad={(e) => {
                const img = e.currentTarget;
                if (img.naturalHeight > img.naturalWidth) img.dataset.retrato = '';
                setProporcoes((p) => ({ ...p, [i]: img.naturalWidth / img.naturalHeight }));
              }}
            />
          </button>
          {f.legenda && <figcaption>{f.legenda}</figcaption>}
        </figure>
      ))}
    </S.Fotos>
  );
};

// A próxima (mais recente) fica à esquerda e a anterior à direita; a seta
// aponta para o lado da coluna.
const Vizinha = ({ noticia, tipo }) => {
  const antiga = tipo === 'anterior';
  const rotulo = antiga ? ROTULOS.anterior : ROTULOS.proxima;
  if (!noticia) {
    return (
      <div>
        <span>
          {antiga ? '' : '← '}
          {rotulo}
          {antiga ? ' →' : ''}
        </span>
        <p>{antiga ? ROTULOS.maisAntiga : ROTULOS.maisRecente}</p>
      </div>
    );
  }
  return (
    <div>
      <S.Acao
        to={enderecoDa(noticia)}
        $voltar={!antiga}
        $direita={antiga}
        aria-label={`${rotulo}: ${noticia.titulo}`}
      >
        {!antiga && <ArrowLeft size={17} aria-hidden="true" />}
        {rotulo}
        {antiga && <ArrowRight size={17} aria-hidden="true" />}
      </S.Acao>
      <h2>{noticia.titulo}</h2>
      <time dateTime={noticia.data.slice(0, 10)}>{dataPorExtenso(noticia.data)}</time>
    </div>
  );
};

export const Materia = () => {
  const { endereco } = useParams();
  const [noticias, setNoticias] = useState(null);
  const [materia, setMateria] = useState(undefined);
  const [aberta, setAberta] = useState(null);
  const [aviso, setAviso] = useState('');
  const artigo = useRef(null);

  const { setTitle, setBgHeader } = useHeaderStates();
  const { token, setError, setLoading } = useStartupState();

  useEffect(() => {
    setError(null);
    if (!token) return;
    setLoading(true);
    setMateria(undefined);
    Promise.all([carregarNoticias(token), carregarMateria(token, endereco)])
      .then(([lista, m]) => {
        setNoticias(lista);
        setMateria(m);
      })
      .catch((e) => {
        console.log(e.message);
        setMateria(null);
      })
      .finally(() => setLoading(false));
  }, [token, endereco]);

  useEffect(() => {
    setTitle(null);
    setBgHeader(null);
  });

  // Cada matéria abre do topo.
  // Com chaves: o efeito não pode devolver o retorno do scrollTo.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [endereco]);

  useEffect(() => {
    if (!materia) return undefined;
    return aplicarMetadados({
      titulo: materia.metadados?.titulo || `${materia.titulo} · Copasul`,
      descricao: materia.metadados?.descricao || materia.resumo,
    });
  }, [materia]);

  useEffect(() => {
    if (!aviso) return undefined;
    const t = setTimeout(() => setAviso(''), 3000);
    return () => clearTimeout(t);
  }, [aviso]);

  // Todas as fotos da matéria, na ordem (capa primeiro), para o visualizador.
  const fotos = useMemo(() => {
    if (!materia) return [];
    const daCapa = materia.capa && materia.capaNaMateria ? [{ id: materia.capa, legenda: materia.legendaCapa }] : [];
    const doCorpo = materia.corpo.flatMap((b) =>
      (b.fotos || []).map((f) => ({ id: f.foto?.[0], legenda: f.legenda || '' })).filter((f) => f.id),
    );
    return [...daCapa, ...doCorpo];
  }, [materia]);

  if (materia === undefined) return <Loading />;
  if (materia === null) return <Navigate to="/noticias" replace />;

  const lista = noticias?.lista || [];
  const posicao = lista.findIndex((n) => n.endereco === materia.endereco);
  const anterior = posicao >= 0 ? lista[posicao + 1] : null;
  const proxima = posicao > 0 ? lista[posicao - 1] : null;

  const comCapa = Boolean(materia.capa && materia.capaNaMateria);
  const mostraResumo = !resumoRepeteOTexto(materia.resumo, materia.corpo);

  const compartilhar = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({ title: materia.titulo, url });
        return;
      } catch (e) {
        if (e?.name === 'AbortError') return;
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      setAviso(ROTULOS.copiado);
    } catch {
      setAviso(ROTULOS.copieOEndereco);
    }
  };

  // Posição de cada bloco de fotos na lista do visualizador.
  let proximaFoto = comCapa ? 1 : 0;

  return (
    <S.Pagina>
      <BarraDeLeitura alvo={artigo} />
      <S.Container>
        <S.TrilhaDaPagina rota="/noticias" atual={materia.titulo} />

        <article ref={artigo}>
          <S.Cabecalho>
            <h1>{materia.titulo}</h1>
            {mostraResumo && <p>{materia.resumo}</p>}
          </S.Cabecalho>

          {comCapa && (
            <S.Capa>
              <div>
                <FotoEnquadrada id={materia.capa} largura={1600} alt={materia.legendaCapa || ''} loading="eager" />
                <button type="button" onClick={() => setAberta(0)} aria-label={ROTULOS.ampliar}>
                  <Maximize2 size={17} />
                </button>
              </div>
              <figcaption>
                <span>{ROTULOS.emImagem}</span>
                {materia.legendaCapa || materia.titulo}
              </figcaption>
            </S.Capa>
          )}

          <S.Leitura>
            <S.Trilho>
              <p>{ROTULOS.publicadoEm}</p>
              <time dateTime={materia.data.slice(0, 10)}>{dataPorExtenso(materia.data)}</time>
              <hr />
              <span>
                {minutosDeLeitura(materia.corpo)} {ROTULOS.minLeitura}
              </span>
              <button type="button" onClick={compartilhar}>
                {ROTULOS.compartilhar} <ArrowUpRight size={13} aria-hidden="true" />
              </button>
            </S.Trilho>

            <div>
              {materia.corpo.map((bloco, i) => {
                if (bloco.fotos) {
                  const lista2 = bloco.fotos
                    .map((f) => ({ id: f.foto?.[0], legenda: f.legenda || '' }))
                    .filter((f) => f.id);
                  const inicio = proximaFoto;
                  proximaFoto += lista2.length;
                  return lista2.length ? (
                    <BlocoFotos key={i} fotos={lista2} inicio={inicio} aoAbrir={setAberta} />
                  ) : null;
                }
                return (
                  <S.Texto
                    key={i}
                    lang="pt-BR"
                    $capitular={i === 0}
                    // HTML vindo do editor de texto do Squidex (conteúdo da própria Copasul).
                    dangerouslySetInnerHTML={{ __html: bloco.texto || '' }}
                  />
                );
              })}
            </div>
          </S.Leitura>
        </article>

        <S.Vizinhas aria-label="Navegar entre notícias">
          <Vizinha noticia={proxima} tipo="proxima" />
          <Vizinha noticia={anterior} tipo="anterior" />
        </S.Vizinhas>
      </S.Container>

      {aberta !== null && fotos.length > 0 && (
        <Visualizador
          titulo={fotos[aberta]?.legenda || materia.titulo}
          fotos={fotos.map((f) => f.id)}
          inicio={aberta}
          ajuda={ROTULOS.ajuda}
          aoFechar={() => setAberta(null)}
        />
      )}

      {aviso && (
        <S.Aviso role="status">{aviso}</S.Aviso>
      )}
    </S.Pagina>
  );
};

// Endereço do modelo antigo: /paginaNoticia/<idUrl>. A notícia é achada pelo
// `enderecoAntigo` que a conversão guardou; sem correspondência, vai para a lista.
export const MateriaAntiga = () => {
  const { idNew } = useParams();
  const navigate = useNavigate();
  const { token } = useStartupState();

  useEffect(() => {
    if (!token) return;
    enderecoDaAntiga(token, decodeURIComponent(idNew))
      .then((novo) => navigate(novo ? `/noticias/${novo}` : '/noticias', { replace: true }))
      .catch(() => navigate('/noticias', { replace: true }));
  }, [token, idNew]);

  return <Loading />;
};
